import React, { useState } from 'react';
import firstBadge from '../../../../assets/first-badge.png';
import secondBadge from '../../../../assets/second-badge.png';
import thirdBadge from '../../../../assets/third-badge.png';
import { RaceStatus, TokenStats } from '../../../../types/race';

import './RaceStandings.css';
import { formatPrice } from '../../../../utils/formatPrice';
import dayjs from 'dayjs';
import { InfoIcon } from 'lucide-react';
import { formatUnits } from 'viem';

const BADGES = [firstBadge, secondBadge, thirdBadge];

type TokenStatsProps = {
  tokenStats: TokenStats[];
  raceStatus: RaceStatus;
  oracleStartTimestamp: bigint;
  oracleEndTimestamp: bigint;
  handicapEnabled: boolean;
};

const RaceStandings = ({
  tokenStats,
  raceStatus,
  oracleStartTimestamp,
  oracleEndTimestamp,
  handicapEnabled,
}: TokenStatsProps) => {
  const [endPriceTooltip, setEndPriceTooltip] = useState(false);

  const renderPrices = (tokenStat: TokenStats) => {
    if (raceStatus === RaceStatus.BETTING) {
      return (
        <div className="flex items-center justify-between">
          <div className="flex flex-col">
            <span className="text-xs text-gray-200">Current Price</span>
            <span className="font-bold">
              {tokenStat.token.marketData && formatPrice(tokenStat.token.marketData.current_price)}
            </span>
            <span className="text-xs text-gray-400">
              {dayjs.unix(Number(oracleStartTimestamp)).format('DDMMMYY HH:mm')}
            </span>
          </div>
        </div>
      );
    } else if (raceStatus === RaceStatus.IN_PROGRESS) {
      return (
        <div className="flex items-center justify-between">
          <div className="flex flex-col">
            <span className="text-xs text-gray-200">Starting Price</span>
            <span className="font-bold">{formatPrice(tokenStat.startPrice)}</span>
            <span className="text-xs text-gray-400">
              {dayjs.unix(Number(oracleStartTimestamp)).format('DDMMMYY HH:mm')}
            </span>
          </div>
          <div className="flex flex-col items-end">
            <span className="text-xs text-gray-200">Current Price</span>
            <span className="font-bold">
              {tokenStat.token.marketData && formatPrice(tokenStat.token.marketData.current_price)}
            </span>
            <span className="text-xs text-gray-400">
              {dayjs.unix(Number(oracleEndTimestamp)).format('DDMMMYY HH:mm')}
            </span>
          </div>
        </div>
      );
    } else {
      return (
        <div className="flex items-center justify-between">
          <div className="flex flex-col">
            <span className="text-xs text-gray-200">Starting Price</span>
            <span className="font-bold">{formatPrice(tokenStat.startPrice)}</span>
            <span className="text-xs text-gray-400">
              {dayjs.unix(Number(oracleStartTimestamp)).format('DDMMMYY HH:mm')}
            </span>
          </div>
          <div className="flex flex-col items-end">
            <div className="flex items-center gap-1">
              <span className="text-xs text-gray-200">Final Price</span>
              {handicapEnabled && (
                <div className="relative">
                  <InfoIcon
                    className="h-3 w-3 text-gray-400"
                    onMouseEnter={() => setEndPriceTooltip(true)}
                    onMouseLeave={() => setEndPriceTooltip(false)}
                  />
                  {endPriceTooltip && (
                    <div className="absolute bottom-3 min-w-max rounded-md bg-gray-800 p-2 text-xs">
                      <span className="block">FDV {formatPrice(tokenStat.endFdv)}</span>
                      <span className="block">
                        30 Day Volatility {Number(formatUnits(tokenStat.endVolatility, 18)).toFixed(2)}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
            <span className="font-bold">{formatPrice(tokenStat.endPrice)}</span>
            <span className="text-xs text-gray-400">
              {dayjs.unix(Number(oracleEndTimestamp)).format('DDMMMYY HH:mm')}
            </span>
          </div>
        </div>
      );
    }
  };

  return (
    <div className="token-stats-grid">
      {tokenStats.map((tokenStat, index) => (
        <div key={tokenStat.token.symbol} className={`token-stat-card relative position-${index + 1}`}>
          <div className="absolute -left-4 -top-4">{index < 3 && <img src={BADGES[index]} alt="Badge" />}</div>
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 text-sm">
                <div className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-full bg-white">
                  <img src={tokenStat.token.marketData?.image} alt={`${tokenStat.token.symbol}`} />
                </div>
                <h3>{tokenStat.token.symbol}</h3>
              </div>
              {raceStatus !== RaceStatus.BETTING && (
                <div
                  className={`rounded px-2 py-0.5 text-sm ${tokenStat.token.score >= 0 ? 'bg-[rgb(16,192,131)]/15 text-[#10b981]' : 'bg-[rgb(239,68,68)]/10 text-[#ef4444]'}`}
                >
                  {tokenStat.token.score > 0 ? '+' : ''}
                  {tokenStat.token.score.toFixed(2)}%
                </div>
              )}
            </div>
            {renderPrices(tokenStat)}
          </div>
        </div>
      ))}
    </div>
  );
};

export default RaceStandings;
